import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { GraduationCap, ArrowRight, Sparkles, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useStudentAuth, studentApi } from "@/lib/studentAuth";
import { formatApiError } from "@/lib/api";

export default function StudentRegister() {
  const { student, register } = useStudentAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", password: "",
    student_class: "", board: "",
    parent_name: "", parent_phone: "",
  });
  const [submitting, setSubmitting] = useState(false);

  if (student && student.id) return <Navigate to="/student" replace />;

  const verifyCode = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      const { data } = await studentApi.post("/enrollment-codes/verify", { code: code.trim().toUpperCase() });
      toast.success("Code accepted!");
      setForm((f) => ({
        ...f,
        student_class: data.class_hint || "",
        board: data.board_hint || "",
      }));
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setVerifying(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.password || !form.student_class || !form.board) {
      toast.error("Please fill all required fields");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await register({ enrollment_code: code.trim().toUpperCase(), ...form });
      toast.success("Account created!");
      nav("/student");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B192C] relative overflow-hidden grid place-items-center px-4 py-10 text-white">
      <div className="vk-grain" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <span className="w-10 h-10 rounded-md bg-white/5 border border-white/10 grid place-items-center">
            <GraduationCap size={22} className="text-[#FF6B35]" />
          </span>
          <div className="font-display text-xl font-bold uppercase tracking-tight">Vikramshila Classes</div>
        </Link>

        {step === 1 && (
          <form onSubmit={verifyCode} data-testid="student-code-form" className="rounded-lg border border-white/10 bg-white/5 p-8 backdrop-blur-xl">
            <div className="flex items-center gap-2 text-[#FF6B35]"><Sparkles size={18} /><span className="text-xs uppercase tracking-widest">Step 1 of 2</span></div>
            <h1 className="mt-3 font-display text-3xl font-800">Enter your enrollment code</h1>
            <p className="mt-2 text-sm text-white/60">Your teacher shared a code with you (e.g. <span className="font-mono text-white/80">VKS-XXXXXX</span>). Enter it below to continue.</p>

            <div className="mt-6">
              <Label className="text-white/80">Enrollment code</Label>
              <Input data-testid="student-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="VKS-XXXXXX" required className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-white/40 font-mono tracking-widest" />
            </div>

            <button type="submit" disabled={verifying} data-testid="student-verify-code" className="vk-btn-primary w-full mt-6 justify-center">
              {verifying ? "Verifying…" : (<>Continue <ArrowRight size={16} /></>)}
            </button>

            <div className="mt-6 text-center text-sm text-white/60">
              Already registered?{" "}
              <Link to="/student/login" className="text-[#FF6B35] hover:underline">Sign in</Link>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submit} data-testid="student-register-form" className="rounded-lg border border-white/10 bg-white/5 p-8 backdrop-blur-xl">
            <button type="button" onClick={() => setStep(1)} className="flex items-center gap-1 text-xs text-white/60 hover:text-white mb-3">
              <ArrowLeft size={12} /> Change code
            </button>
            <div className="flex items-center gap-2 text-[#FF6B35]"><Sparkles size={18} /><span className="text-xs uppercase tracking-widest">Step 2 of 2</span></div>
            <h1 className="mt-3 font-display text-2xl font-800">Create your account</h1>

            <div className="mt-5 space-y-4">
              <div>
                <Label className="text-white/80">Student name *</Label>
                <Input data-testid="reg-name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required className="mt-1 bg-white/10 border-white/20 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white/80">Phone *</Label>
                  <Input data-testid="reg-phone" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} required className="mt-1 bg-white/10 border-white/20 text-white" />
                </div>
                <div>
                  <Label className="text-white/80">Password *</Label>
                  <Input type="password" data-testid="reg-password" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} required className="mt-1 bg-white/10 border-white/20 text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white/80">Class *</Label>
                  <Select value={form.student_class} onValueChange={(v) => setForm(f => ({ ...f, student_class: v }))}>
                    <SelectTrigger data-testid="reg-class" className="mt-1 bg-white/10 border-white/20 text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {["VI","VII","VIII","IX","X"].map(c => (<SelectItem key={c} value={c}>Class {c}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/80">Board *</Label>
                  <Select value={form.board} onValueChange={(v) => setForm(f => ({ ...f, board: v }))}>
                    <SelectTrigger data-testid="reg-board" className="mt-1 bg-white/10 border-white/20 text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CBSE">CBSE</SelectItem>
                      <SelectItem value="ICSE">ICSE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white/80">Parent name</Label>
                  <Input data-testid="reg-parent-name" value={form.parent_name} onChange={(e) => setForm(f => ({ ...f, parent_name: e.target.value }))} className="mt-1 bg-white/10 border-white/20 text-white" />
                </div>
                <div>
                  <Label className="text-white/80">Parent phone</Label>
                  <Input data-testid="reg-parent-phone" value={form.parent_phone} onChange={(e) => setForm(f => ({ ...f, parent_phone: e.target.value }))} className="mt-1 bg-white/10 border-white/20 text-white" />
                </div>
              </div>
            </div>

            <button type="submit" disabled={submitting} data-testid="reg-submit" className="vk-btn-primary w-full mt-6 justify-center">
              {submitting ? "Creating…" : (<>Create account <ArrowRight size={16} /></>)}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
